import { motion } from 'framer-motion'
import { WHEEL_NUMBERS, getNumberColor } from '../utils/ruletaLogic'

interface RuletaWheelProps {
  rotation: number
  isSpinning: boolean
  winningNumber: number | null
}

export function RuletaWheel({ rotation, isSpinning, winningNumber }: RuletaWheelProps) {
  const numberOfSlots = WHEEL_NUMBERS.length // 37 for European
  const slotAngle = 360 / numberOfSlots

  return (
    <div className="relative w-64 h-64 md:w-80 md:h-80">
      {/* Outer decorative ring */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: 'linear-gradient(135deg, #8b6914 0%, #d4af37 50%, #8b6914 100%)',
          boxShadow: '0 0 30px rgba(212, 175, 55, 0.5), inset 0 0 20px rgba(0,0,0,0.3)',
        }}
      />

      {/* Main wheel container */}
      <div className="absolute inset-2 rounded-full overflow-hidden bg-casino-black">
        {/* Spinning wheel */}
        <motion.div
          className="w-full h-full rounded-full relative"
          animate={{ rotate: rotation }}
          transition={{
            duration: isSpinning ? 5 : 0,
            ease: [0.25, 0.1, 0.25, 1], // Custom easing for realistic spin
          }}
        >
          {/* SVG Wheel */}
          <svg viewBox="0 0 200 200" className="w-full h-full">
            {/* Wheel segments */}
            {WHEEL_NUMBERS.map((number, index) => {
              const startAngle = index * slotAngle - 90 - slotAngle / 2
              const endAngle = startAngle + slotAngle
              const color = getNumberColor(number)

              // Calculate arc path
              const startRad = (startAngle * Math.PI) / 180
              const endRad = (endAngle * Math.PI) / 180

              const x1 = 100 + 85 * Math.cos(startRad)
              const y1 = 100 + 85 * Math.sin(startRad)
              const x2 = 100 + 85 * Math.cos(endRad)
              const y2 = 100 + 85 * Math.sin(endRad)

              const x3 = 100 + 30 * Math.cos(endRad)
              const y3 = 100 + 30 * Math.sin(endRad)
              const x4 = 100 + 30 * Math.cos(startRad)
              const y4 = 100 + 30 * Math.sin(startRad)

              const largeArcFlag = slotAngle > 180 ? 1 : 0

              const pathD = `
                M ${x1} ${y1}
                A 85 85 0 ${largeArcFlag} 1 ${x2} ${y2}
                L ${x3} ${y3}
                A 30 30 0 ${largeArcFlag} 0 ${x4} ${y4}
                Z
              `

              // Text position
              const midAngle = ((startAngle + endAngle) / 2 * Math.PI) / 180
              const textX = 100 + 60 * Math.cos(midAngle)
              const textY = 100 + 60 * Math.sin(midAngle)
              const textRotation = (startAngle + endAngle) / 2 + 90

              return (
                <g key={number}>
                  <path
                    d={pathD}
                    fill={color === 'red' ? '#c41e3a' : color === 'black' ? '#1a1a1a' : '#0d8c0d'}
                    stroke="#d4af37"
                    strokeWidth="0.5"
                  />
                  <text
                    x={textX}
                    y={textY}
                    fill="white"
                    fontSize="8"
                    fontWeight="bold"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    transform={`rotate(${textRotation}, ${textX}, ${textY})`}
                  >
                    {number}
                  </text>
                </g>
              )
            })}

            {/* Center decoration */}
            <circle cx="100" cy="100" r="28" fill="#2a2a2a" stroke="#d4af37" strokeWidth="2" />
            <circle cx="100" cy="100" r="20" fill="#1a1a1a" />
            <circle cx="100" cy="100" r="5" fill="#d4af37" />
          </svg>
        </motion.div>
      </div>

      {/* Ball indicator (top) */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1">
        <div
          className="w-4 h-6"
          style={{
            clipPath: 'polygon(50% 100%, 0% 0%, 100% 0%)',
            background: 'linear-gradient(180deg, #d4af37 0%, #8b6914 100%)',
          }}
        />
      </div>

      {/* Winning number display */}
      {winningNumber !== null && !isSpinning && (
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
        >
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white shadow-lg"
            style={{
              backgroundColor:
                getNumberColor(winningNumber) === 'red'
                  ? '#c41e3a'
                  : getNumberColor(winningNumber) === 'black'
                  ? '#1a1a1a'
                  : '#0d8c0d',
              border: '3px solid #d4af37',
              boxShadow: '0 0 20px rgba(212, 175, 55, 0.8)',
            }}
          >
            {winningNumber}
          </div>
        </motion.div>
      )}
    </div>
  )
}
